import { useEffect, useState, useRef } from "react";
import Layout from "@/components/Layout";
import { Card, Button, Empty, Spinner, Badge, Select } from "@/components/ui/Primitives";
import { api, formatErr } from "@/lib/api";
import { Upload, FileText, CheckCircle2, AlertCircle, ChevronDown, ChevronRight, Link2, Play, AlertTriangle, Pencil, Plus, Trash2, X, HeartPulse, UserX, RefreshCw } from "lucide-react";

const HM = (min) => {
  const m = Math.max(0, Math.round(Number(min) || 0));
  return `${Math.floor(m / 60)}h${String(m % 60).padStart(2, "0")}`;
};

const TOLERANCIA_MIN = 10;
const INTERVALO_ALMOCO_MIN = 60;

// Recomputa 1 dia com marcações editadas (mesma lógica do parser Python)
function recalcDia(dia, marcacoesHHMMSS) {
  const marcs = [...marcacoesHHMMSS].filter(Boolean).sort();
  const novo = {
    ...dia,
    marcacoes: marcs,
    trabalhadas_min: 0,
    atraso_min: 0,
    he50_min: 0,
    he100_min: 0,
    falta_dia: false,
    inconsistente: false,
    aviso: "",
    editado: true,
  };
  const nMarc = marcs.length;
  const esperado = dia.esperado_min || 0;
  const tipoDia = dia.tipo_dia || "util";

  const toMin = (hhmmss) => {
    const [h, m, s = "0"] = hhmmss.split(":");
    return parseInt(h) * 60 + parseInt(m) + parseInt(s) / 60;
  };

  if (nMarc === 0) {
    if (tipoDia === "util") novo.falta_dia = true;
    return novo;
  }

  if (nMarc % 2 === 1) {
    novo.inconsistente = true;
    if (nMarc === 1) {
      novo.aviso = `Só 1 marcação (${marcs[0]}) — adicione a saída`;
      return novo;
    }
    // 3 marcações — estima (ultima - primeira) - 1h
    const est = Math.max(0, (toMin(marcs[marcs.length - 1]) - toMin(marcs[0])) - INTERVALO_ALMOCO_MIN);
    novo.trabalhadas_min = Math.round(est);
    novo.aviso = `3 marcações — estimativa: ${novo.trabalhadas_min}min assumindo 1h de almoço`;
    const exc = novo.trabalhadas_min - esperado;
    if (tipoDia === "domingo" || tipoDia === "feriado") novo.he100_min = novo.trabalhadas_min;
    else if (exc > 0) novo.he50_min = exc;
    return novo;
  }

  // Pares
  let total = 0;
  for (let i = 0; i < marcs.length; i += 2) total += toMin(marcs[i + 1]) - toMin(marcs[i]);
  novo.trabalhadas_min = Math.round(total);

  if (nMarc === 2 && tipoDia === "util" && novo.trabalhadas_min < esperado - 60) {
    novo.inconsistente = true;
    novo.aviso = `Só 2 marcações (${marcs.join(" · ")}) — possivelmente esqueceu almoço/retorno`;
    return novo;
  }

  const exc = novo.trabalhadas_min - esperado;
  if (tipoDia === "domingo" || tipoDia === "feriado") {
    novo.he100_min = novo.trabalhadas_min;
  } else if (exc >= 0) {
    novo.he50_min = exc;
  } else {
    const deficit = -exc;
    novo.atraso_min = deficit <= TOLERANCIA_MIN ? 0 : deficit;
  }
  return novo;
}

// Recomputa totais do funcionário
function recalcTotais(r) {
  let trab = 0, he50 = 0, he100 = 0, atr = 0, faltas = 0, esperado = 0, incons = 0;
  const avisos = [];
  for (const d of r.dias) {
    trab += d.trabalhadas_min || 0;
    he50 += d.he50_min || 0;
    he100 += d.he100_min || 0;
    atr += d.atraso_min || 0;
    esperado += d.esperado_min || 0;
    if (d.falta_dia) faltas += 1;
    // Conta como pendência: dias inconsistentes NÃO editados OU em_andamento não resolvidos
    const naoResolvido = !d.editado;
    if (naoResolvido && (d.inconsistente || d.em_andamento)) {
      incons += 1;
      if (d.aviso) avisos.push({ data: d.data, aviso: d.aviso });
    }
  }
  const hm = (m) => `${Math.floor(m / 60)}h${String(m % 60).padStart(2, "0")}`;
  return {
    ...r,
    total_trabalhadas_h: +(trab / 60).toFixed(2),
    total_esperado_h: +(esperado / 60).toFixed(2),
    he50_h: +(he50 / 60).toFixed(2),
    he100_h: +(he100 / 60).toFixed(2),
    atraso_min: atr,
    faltas_dias: faltas,
    inconsistencias: incons,
    he50_fmt: hm(he50),
    he100_fmt: hm(he100),
    atraso_fmt: `${atr}min`,
    avisos,
  };
}

export default function Ponto() {
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [funcs, setFuncs] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [competencia, setCompetencia] = useState(new Date().toISOString().slice(0, 7));
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [editDay, setEditDay] = useState(null); // { funcSlug, dia }
  // Marca resultado como "sujo" (editado pelo usuário) — auto-save só dispara quando true.
  // Evita sobrescrever o snapshot no banco logo após o load (que poderia reduzir dados
  // caso o filtro por competência remova algo indevidamente).
  const resultDirty = useRef(false);

  const openEditDay = (fSlug, dia) => setEditDay({ funcSlug: fSlug, dia });
  const saveEditDay = (novasMarcs) => {
    const { funcSlug, dia } = editDay;
    resultDirty.current = true;
    setResult((prev) => {
      const funcionarios = prev.funcionarios.map((r) => {
        if (r.slug !== funcSlug) return r;
        const dias = r.dias.map((d) => d.data === dia.data ? recalcDia(d, novasMarcs) : d);
        const updated = recalcTotais({ ...r, dias });
        return updated;
      });
      return { ...prev, funcionarios };
    });
    setEditDay(null);
  };

  const aplicarAtestado = (fSlug, dataDia) => {
    resultDirty.current = true;
    setResult((prev) => ({
      ...prev,
      funcionarios: prev.funcionarios.map((r) => {
        if (r.slug !== fSlug) return r;
        const dias = r.dias.map((d) => {
          if (d.data !== dataDia) return d;
          return {
            ...d,
            trabalhadas_min: d.esperado_min || 0,
            atraso_min: 0,
            he50_min: 0,
            he100_min: 0,
            falta_dia: false,
            inconsistente: false,
            em_andamento: false,
            aviso: "✓ Atestado médico aplicado",
            editado: true,
            resolucao: "atestado",
          };
        });
        return recalcTotais({ ...r, dias });
      }),
    }));
  };

  // Descarta o aviso sem alterar horas trabalhadas — só silencia o warning.
  // Útil quando o RH quer manter as marcações existentes mas remover o alerta amarelo.
  const descartarAviso = (fSlug, dataDia) => {
    resultDirty.current = true;
    setResult((prev) => ({
      ...prev,
      funcionarios: prev.funcionarios.map((r) => {
        if (r.slug !== fSlug) return r;
        const dias = r.dias.map((d) => {
          if (d.data !== dataDia) return d;
          return {
            ...d,
            inconsistente: false,
            em_andamento: false,
            aviso: "✓ Aviso descartado manualmente",
            editado: true,
            resolucao: "aviso_descartado",
          };
        });
        return recalcTotais({ ...r, dias });
      }),
    }));
  };

  const aplicarFaltouRestoDia = (fSlug, dataDia) => {
    resultDirty.current = true;
    setResult((prev) => ({
      ...prev,
      funcionarios: prev.funcionarios.map((r) => {
        if (r.slug !== fSlug) return r;
        const dias = r.dias.map((d) => {
          if (d.data !== dataDia) return d;
          // Calcula quanto realmente trabalhou pelas marcações pares
          const marcs = [...(d.marcacoes || [])].sort();
          const toMin = (hhmmss) => {
            const [h, m, s = "0"] = hhmmss.split(":");
            return parseInt(h) * 60 + parseInt(m) + parseInt(s) / 60;
          };
          let trab = 0;
          const limite = marcs.length - (marcs.length % 2); // ignora marcação ímpar (sem par)
          for (let i = 0; i < limite; i += 2) trab += toMin(marcs[i + 1]) - toMin(marcs[i]);
          trab = Math.round(trab);
          const esperado = d.esperado_min || 0;
          const isUtil = d.tipo_dia === "util" || d.tipo_dia === "sabado";
          const hm = (m) => `${Math.floor(m / 60)}h${String(m % 60).padStart(2, "0")}`;

          // Sem marcações válidas → falta cheia (1 dia)
          if (trab <= 0) {
            return {
              ...d, trabalhadas_min: 0, atraso_min: 0, he50_min: 0, he100_min: 0,
              falta_dia: isUtil, inconsistente: false, em_andamento: false,
              aviso: "✕ Falta cheia (sem marcações válidas)",
              editado: true, resolucao: "falta",
            };
          }
          // Trabalhou parte do dia → falta parcial (atraso proporcional)
          const deficit = Math.max(0, esperado - trab);
          return {
            ...d,
            trabalhadas_min: trab,
            atraso_min: deficit,
            he50_min: 0, he100_min: 0,
            falta_dia: false,
            inconsistente: false,
            em_andamento: false,
            aviso: `⏸ Falta parcial — trabalhou ${hm(trab)} · faltou ${hm(deficit)}`,
            editado: true,
            resolucao: "falta_parcial",
          };
        });
        return recalcTotais({ ...r, dias });
      }),
    }));
  };

  useEffect(() => {
    api.get("/rh/funcionarios").then((r) => setFuncs(r.data.items || r.data || [])).catch(() => {});
  }, []);

  // Carrega snapshot persistido ao trocar competência
  useEffect(() => {
    let cancelled = false;
    resultDirty.current = false; // reset dirty ao trocar mês
    (async () => {
      try {
        const { data } = await api.get("/rh/ponto/snapshot", { params: { competencia } });
        if (!cancelled && data && data.funcionarios) {
          setResult({
            funcionarios: data.funcionarios,
            n_total: data.n_total ?? data.funcionarios.length,
            n_com_match: data.n_com_match ?? data.funcionarios.filter((r) => r.match_funcionario_id).length,
            aplicado_at: data.aplicado_at || null,
            aplicados_count: data.aplicados_count || 0,
          });
          // load NÃO marca como dirty — evita sobrescrever o banco após um simples GET
          resultDirty.current = false;
        }
      } catch (e) {
        // 404 = sem snapshot para esta competência (limpa)
        if (!cancelled && e?.response?.status === 404) setResult(null);
      }
    })();
    return () => { cancelled = true; };
  }, [competencia]);

  // Persiste snapshot no backend (debounced) — SÓ quando houve edição do usuário.
  const saveSnapshot = async (payload) => {
    if (!payload || !payload.funcionarios) return;
    try {
      await api.post("/rh/ponto/snapshot", {
        competencia,
        funcionarios: payload.funcionarios,
        n_total: payload.n_total,
        n_com_match: payload.n_com_match,
      });
    } catch (_) { /* silencioso — não bloqueia a UI */ }
  };

  // Sempre que o result mudar E tiver sido editado, salva. Debounce simples de 400ms.
  useEffect(() => {
    if (!result) return;
    if (!resultDirty.current) return; // não salva load inicial
    const t = setTimeout(() => saveSnapshot(result), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, competencia]);

  const limparPonto = async () => {
    if (!window.confirm(`Limpar o ponto de ${competencia}?\nOs dados salvos serão apagados e você poderá subir um novo arquivo.`)) return;
    try {
      await api.delete("/rh/ponto/snapshot", { params: { competencia } });
      setResult(null);
      setFile(null);
      setApplyResult(null);
    } catch (e) { alert(formatErr(e)); }
  };

  const handleParse = async () => {
    if (!file) { setErr("Selecione o arquivo do relógio de ponto"); return; }
    setParsing(true); setErr(""); setResult(null); setApplyResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      // Envia a competência selecionada — o backend filtra as marcações para APENAS este mês.
      // Assim, se o arquivo trouxer dias de meses vizinhos, eles são descartados
      // e a competência escolhida pelo usuário é respeitada.
      if (competencia) form.append("competencia", competencia);
      const { data } = await api.post("/rh/ponto/preview", form, { headers: { "Content-Type": "multipart/form-data" } });
      // Se o backend retornar competência (auto-detectada quando o usuário não enviou uma),
      // sincroniza o input. Se o usuário já escolheu uma, mantém a escolha dele.
      if (!competencia && data.competencia) {
        setCompetencia(data.competencia);
      }
      setResult(data);
      if (data.preserved_edits > 0) {
        setTimeout(() => alert(`Arquivo importado! ${data.preserved_edits} dia(s) editado(s) manualmente foram preservados (não foram sobrescritos).`), 100);
      }
    } catch (e) { setErr(formatErr(e)); }
    finally { setParsing(false); }
  };

  const vincular = async (fSlug, funcionarioId) => {
    const row = result.funcionarios.find((r) => r.slug === fSlug);
    if (!row || !funcionarioId) return;
    // Grava biometria_id (usa o primeiro id da biometria)
    const bioId = row.biometria_ids[0];
    try {
      await api.post("/rh/ponto/vincular", { funcionario_id: funcionarioId, biometria_id: bioId });
      // Atualiza estado local
      resultDirty.current = true;
      setResult((prev) => ({
        ...prev,
        funcionarios: prev.funcionarios.map((r) => r.slug === fSlug ? { ...r, match_funcionario_id: funcionarioId, match_funcionario_nome: funcs.find((f) => f.id === funcionarioId)?.nome } : r),
        n_com_match: prev.funcionarios.filter((r) => r.slug === fSlug ? funcionarioId : r.match_funcionario_id).length,
      }));
    } catch (e) { alert(formatErr(e)); }
  };

  const setMatchInline = (fSlug, funcionarioId) => {
    resultDirty.current = true;
    setResult((prev) => ({
      ...prev,
      funcionarios: prev.funcionarios.map((r) => r.slug === fSlug ? { ...r, match_funcionario_id: funcionarioId || null, match_funcionario_nome: funcionarioId ? (funcs.find((f) => f.id === funcionarioId)?.nome || "") : null } : r),
    }));
  };

  const aplicar = async () => {
    if (!result) return;
    const comMatch = result.funcionarios.filter((r) => r.match_funcionario_id);
    if (comMatch.length === 0) { alert("Vincule ao menos 1 funcionário ao cadastro antes de aplicar."); return; }
    if (!window.confirm(`Aplicar ajustes de ponto na folha ${competencia} para ${comMatch.length} funcionário(s)?\n\nAs rubricas anteriores geradas por ponto serão substituídas.`)) return;
    setApplying(true); setApplyResult(null);
    try {
      const payload = {
        competencia,
        remover_anteriores: true,
        funcionarios: comMatch.map((r) => ({
          funcionario_id: r.match_funcionario_id,
          nome: r.nome,
          he50_min: Math.round((r.he50_h || 0) * 60),
          he100_min: Math.round((r.he100_h || 0) * 60),
          atraso_min: r.atraso_min || 0,
          faltas_dias: r.faltas_dias || 0,
          dias_com_marcacoes: r.dias_com_marcacoes || 0,
        })),
      };
      const { data } = await api.post("/rh/ponto/aplicar", payload);
      setApplyResult(data);
      // O backend já persistiu tudo (aplicado_at, rubricas na folha). Não deixamos o auto-save
      // do snapshot disparar depois, senão ele faria um POST /snapshot que apagaria o `aplicado_at`
      // recém-setado (bug histórico).
      resultDirty.current = false;
      // Marca localmente como aplicado (o backend já persistiu)
      setResult((prev) => prev ? { ...prev, aplicado_at: new Date().toISOString(), aplicados_count: data.aplicados } : prev);
    } catch (e) { alert(formatErr(e)); }
    finally { setApplying(false); }
  };

  return (
    <Layout
      title="Ponto Eletrônico"
      subtitle="Importe as marcações da biometria e aplique automaticamente na folha de pagamento"
    >
      {err && <div className="text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md mb-4">{err}</div>}

      {/* Seletor de competência (sempre visível) */}
      <Card className="p-4 mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-500">Competência</div>
            <input
              type="month"
              value={competencia}
              onChange={(e) => setCompetencia(e.target.value)}
              className="mt-1 text-sm font-mono border border-ink-200 rounded-md px-2 py-1.5"
              data-testid="ponto-competencia-top"
            />
          </div>
          {result && (
            <div className="flex items-center gap-2 ml-auto flex-wrap">
              <Badge tone="success">Ponto salvo · {result.n_total || 0} funcionário{result.n_total > 1 ? "s" : ""}</Badge>
              {result.aplicado_at ? (
                <Badge tone="ok">
                  <CheckCircle2 size={12} className="inline mr-1" />
                  Aplicado na folha · {new Date(result.aplicado_at).toLocaleString("pt-BR")}
                </Badge>
              ) : (
                <Badge tone="danger">
                  <AlertCircle size={12} className="inline mr-1" />
                  Não aplicado na folha
                </Badge>
              )}
              <label className="inline-flex items-center gap-2 cursor-pointer px-3 py-2 rounded-md text-sm font-medium bg-brand-500 text-white hover:bg-brand-600 transition">
                <RefreshCw size={14} />
                {file ? file.name.slice(0, 20) : "Atualizar arquivo"}
                <input
                  type="file"
                  accept=".txt,.log,.csv"
                  className="hidden"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  data-testid="ponto-file-reupload"
                />
              </label>
              {file && (
                <Button onClick={handleParse} disabled={parsing} data-testid="ponto-parse-reupload">
                  {parsing ? <Spinner size={14} /> : <Play size={14} />} Analisar
                </Button>
              )}
              <Button variant="secondary" onClick={limparPonto} data-testid="ponto-limpar">
                <Trash2 size={14} /> Limpar tudo
              </Button>
            </div>
          )}
        </div>
      </Card>

      {/* Upload — só aparece se não há snapshot para essa competência */}
      {!result && (
      <Card className="p-6 mb-6">
        <h3 className="font-heading font-bold text-ink-900 mb-3">1. Enviar arquivo da biometria</h3>
        <p className="text-xs text-ink-500 mb-4">
          Formatos aceitos: .txt / .log de sistemas ZKTeco, Hikvision e similares.<br />
          Estrutura esperada: <code className="text-[11px]">ID Nome Departamento YYYY-MM-DD HH:MM:SS Dispositivo</code>
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex-1 min-w-[240px] flex items-center gap-3 px-3 py-3 border-2 border-dashed border-ink-300 rounded-lg cursor-pointer hover:border-brand-500 hover:bg-brand-500/5 transition-colors">
            <Upload size={18} className="text-brand-500" />
            <span className="text-sm text-ink-700 truncate">
              {file ? file.name : "Clique para escolher o arquivo…"}
            </span>
            <input
              type="file"
              accept=".txt,.log,.csv"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="ponto-file"
            />
          </label>
          <Button onClick={handleParse} disabled={!file || parsing} data-testid="ponto-parse">
            {parsing ? <Spinner size={14} /> : <Play size={14} />} Analisar
          </Button>
        </div>
      </Card>
      )}

      {parsing && (
        <div className="flex justify-center py-8"><Spinner size={26} /></div>
      )}

      {result && (
        <>
          {/* Resumo */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <Kpi label="Funcionários" value={result.n_total} />
            <Kpi label="Vinculados" value={result.n_com_match} tone={result.n_com_match === result.n_total ? "ok" : "warn"} />
            <Kpi label="Sem vínculo" value={result.n_total - result.n_com_match} tone={result.n_com_match === result.n_total ? "neutral" : "danger"} />
            <Kpi label="Inconsistências" value={result.funcionarios.reduce((a, r) => a + (r.inconsistencias || 0), 0)} tone={result.funcionarios.some((r) => r.inconsistencias > 0) ? "warn" : "ok"} />
          </div>

          {/* Banner de avisos globais */}
          {result.funcionarios.some((r) => r.inconsistencias > 0) && (
            <div className="mb-4 p-4 rounded-xl bg-amber-50 border border-amber-300 flex items-start gap-3" data-testid="banner-inconsistencias">
              <AlertTriangle className="text-warn shrink-0 mt-0.5" size={20} />
              <div className="flex-1">
                <div className="font-heading font-bold text-amber-900">
                  Atenção: {result.funcionarios.reduce((a, r) => a + (r.inconsistencias || 0), 0)} inconsistências encontradas
                </div>
                <p className="text-sm text-amber-900/80 mt-1">
                  Alguns funcionários esqueceram de bater ponto em determinados dias, ou o arquivo foi exportado no meio do dia.
                  <strong> Nenhum atraso foi calculado automaticamente</strong> para esses dias — revise cada caso antes de aplicar.
                  Expanda o funcionário para ver os detalhes por dia.
                </p>
              </div>
            </div>
          )}

          {/* Tabela consolidada */}
          <Card className="overflow-hidden mb-4">
            <div className="px-6 py-3 border-b border-ink-200 flex items-center gap-2 bg-stone-50">
              <FileText size={16} className="text-brand-500" />
              <h3 className="font-heading font-bold text-ink-900">2. Consolidado por funcionário</h3>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-ink-200">
                <tr className="text-left text-xs font-bold uppercase tracking-wider text-ink-500">
                  <th className="px-4 py-3 w-8"></th>
                  <th className="px-4 py-3">Nome (biometria)</th>
                  <th className="px-4 py-3">Vincular ao cadastro</th>
                  <th className="px-4 py-3 text-right">Trab.</th>
                  <th className="px-4 py-3 text-right text-emerald-700">HE 50%</th>
                  <th className="px-4 py-3 text-right text-emerald-700">HE 100%</th>
                  <th className="px-4 py-3 text-right text-danger">Atrasos</th>
                  <th className="px-4 py-3 text-right text-danger">Faltas</th>
                  <th className="px-4 py-3 text-right text-warn">Avisos</th>
                </tr>
              </thead>
              <tbody>
                {result.funcionarios.map((r) => {
                  const isOpen = expanded === r.slug;
                  const hasMatch = !!r.match_funcionario_id;
                  return (
                    <>
                      <tr key={r.slug} className={`border-b border-ink-200 ${hasMatch ? "" : "bg-amber-50/40"}`}>
                        <td className="px-4 py-3">
                          <button onClick={() => setExpanded(isOpen ? null : r.slug)} className="text-ink-500">
                            {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium capitalize">{r.nome}</div>
                          <div className="text-[10px] text-ink-500 font-mono">IDs biometria: {r.biometria_ids.join(", ")}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1 items-center">
                            <Select
                              value={r.match_funcionario_id || ""}
                              onChange={(e) => setMatchInline(r.slug, e.target.value)}
                              className="text-xs"
                              data-testid={`match-${r.slug}`}
                            >
                              <option value="">— sem vínculo —</option>
                              {funcs.map((f) => (
                                <option key={f.id} value={f.id}>{f.nome}</option>
                              ))}
                            </Select>
                            {r.match_funcionario_id && (
                              <button
                                onClick={() => vincular(r.slug, r.match_funcionario_id)}
                                className="p-1.5 rounded hover:bg-brand-500/10 text-brand-500 shrink-0"
                                title="Salvar vínculo permanente"
                                data-testid={`save-${r.slug}`}
                              >
                                <Link2 size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 font-mono text-right">{r.total_trabalhadas_h}h</td>
                        <td className="px-4 py-3 font-mono text-right text-emerald-700 font-semibold">{r.he50_fmt}</td>
                        <td className="px-4 py-3 font-mono text-right text-emerald-700 font-semibold">{r.he100_fmt}</td>
                        <td className="px-4 py-3 font-mono text-right text-danger">{r.atraso_fmt}</td>
                        <td className="px-4 py-3 font-mono text-right text-danger">{r.faltas_dias > 0 ? `${r.faltas_dias}d` : "—"}</td>
                        <td className="px-4 py-3 text-right">
                          {r.inconsistencias > 0 ? (
                            <button
                              onClick={() => setExpanded(isOpen ? null : r.slug)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-amber-100 text-amber-800 hover:bg-amber-200 text-xs font-bold"
                              title="Ver avisos"
                              data-testid={`avisos-${r.slug}`}
                            >
                              <AlertTriangle size={12} /> {r.inconsistencias}
                            </button>
                          ) : (
                            <span className="text-emerald-600"><CheckCircle2 size={16} className="inline" /></span>
                          )}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr key={r.slug + "-det"}>
                          <td colSpan={9} className="px-8 py-4 bg-stone-50">
                            {r.avisos?.length > 0 && (
                              <div className="mb-3 p-3 rounded-lg bg-amber-50 border border-amber-300">
                                <div className="flex items-center gap-2 text-amber-900 font-bold text-sm mb-2">
                                  <AlertTriangle size={14} /> Avisos que precisam da sua atenção
                                </div>
                                <ul className="space-y-1 text-xs text-amber-900/90">
                                  {r.avisos.map((a, ix) => (
                                    <li key={ix} className="flex items-start gap-2">
                                      <span className="text-warn mt-0.5">•</span>
                                      <span>{a.aviso}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            <div className="text-[10px] font-bold uppercase text-ink-500 mb-2">Marcações por dia</div>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 text-xs">
                              {r.dias.map((d) => {
                                const bg = d.em_andamento ? "bg-sky-50 border-sky-300"
                                  : d.falta_dia ? "bg-red-50 border-red-200"
                                  : d.inconsistente ? "bg-amber-50 border-amber-300"
                                  : d.editado ? "bg-blue-50 border-blue-300"
                                  : d.tipo_dia === "domingo" || d.tipo_dia === "feriado" ? "bg-stone-100 border-ink-200"
                                  : "bg-white border-ink-200";
                                return (
                                  <div key={d.data} className={`p-2 rounded border ${bg} relative group`}>
                                    <div className="flex justify-between items-center">
                                      <span className="font-mono font-bold">{d.data.slice(-2)}/{d.data.slice(5, 7)}</span>
                                      <div className="flex items-center gap-1">
                                        <span className="text-[10px] text-ink-500">{d.dia_semana}{d.tipo_dia === "feriado" ? " (feriado)" : ""}</span>
                                        <button
                                          onClick={() => openEditDay(r.slug, d)}
                                          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-brand-500/10 text-brand-500"
                                          title="Editar marcações"
                                          data-testid={`edit-${r.slug}-${d.data}`}
                                        >
                                          <Pencil size={11} />
                                        </button>
                                      </div>
                                    </div>
                                    {d.editado && (
                                      <div className="text-[9px] text-blue-700 font-bold uppercase mt-0.5">
                                        {d.resolucao === "atestado" ? "🩹 Atestado"
                                          : d.resolucao === "falta" ? "✕ Falta cheia"
                                          : d.resolucao === "falta_parcial" ? "⏸ Falta parcial"
                                          : d.resolucao === "aviso_descartado" ? "✓ Aviso descartado"
                                          : "✏ Editado"}
                                      </div>
                                    )}
                                    {d.em_andamento && (
                                      <div className="text-[9px] text-sky-700 font-bold uppercase mt-0.5">
                                        ⏱ Em andamento
                                      </div>
                                    )}
                                    {(d.inconsistente || d.em_andamento) && !d.editado && (
                                      <div className="flex flex-wrap gap-1 mt-1.5">
                                        <button
                                          onClick={() => aplicarAtestado(r.slug, d.data)}
                                          className="flex-1 min-w-[70px] px-1.5 py-1 rounded bg-emerald-100 hover:bg-emerald-200 text-emerald-800 text-[10px] font-semibold flex items-center justify-center gap-1"
                                          title="Aplicar atestado — considera o dia inteiro trabalhado"
                                          data-testid={`atestado-${r.slug}-${d.data}`}
                                        >
                                          <HeartPulse size={10} /> Atestado
                                        </button>
                                        <button
                                          onClick={() => aplicarFaltouRestoDia(r.slug, d.data)}
                                          className="flex-1 min-w-[70px] px-1.5 py-1 rounded bg-red-100 hover:bg-red-200 text-red-800 text-[10px] font-semibold flex items-center justify-center gap-1"
                                          title="Não voltou mais — considera as horas trabalhadas e desconta só o que faltou"
                                          data-testid={`faltou-${r.slug}-${d.data}`}
                                        >
                                          <UserX size={10} /> Não voltou mais
                                        </button>
                                        <button
                                          onClick={() => descartarAviso(r.slug, d.data)}
                                          className="flex-1 min-w-[70px] px-1.5 py-1 rounded bg-stone-100 hover:bg-stone-200 text-ink-700 text-[10px] font-semibold flex items-center justify-center gap-1"
                                          title="Descartar aviso — mantém as horas atuais e só silencia o alerta"
                                          data-testid={`descartar-${r.slug}-${d.data}`}
                                        >
                                          <X size={10} /> Descartar aviso
                                        </button>
                                      </div>
                                    )}
                                    {d.falta_dia ? (
                                      <div className="text-danger text-[11px] font-semibold mt-1">FALTOU</div>
                                    ) : d.marcacoes.length === 0 ? (
                                      <div className="text-ink-500 text-[10px] italic mt-1">— sem marcações —</div>
                                    ) : (
                                      <>
                                        <div className="text-[10px] font-mono text-ink-500 leading-tight mt-1">
                                          {d.marcacoes.join(" · ")}
                                        </div>
                                        <div className="flex justify-between text-[10px] mt-1">
                                          <span>Trab: <span className="font-mono">{HM(d.trabalhadas_min)}</span></span>
                                          {d.he50_min > 0 && <span className="text-emerald-700">+HE50 {HM(d.he50_min)}</span>}
                                          {d.he100_min > 0 && <span className="text-emerald-700">+HE100 {HM(d.he100_min)}</span>}
                                          {d.atraso_min > 0 && <span className="text-danger">-{d.atraso_min}min</span>}
                                        </div>
                                        {d.inconsistente && (
                                          <div className="text-warn text-[10px] mt-1 flex items-start gap-1">
                                            <AlertTriangle size={10} className="shrink-0 mt-0.5" />
                                            <span>{d.aviso || "marcações inconsistentes"}</span>
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </Card>

          {/* Aplicar */}
          <Card className="p-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h3 className="font-heading font-bold text-ink-900 mb-1">3. Aplicar na folha de pagamento</h3>
                <p className="text-xs text-ink-500">
                  Vai criar automaticamente as rubricas de HE 50%, HE 100%, atrasos e faltas nas folhas em aberto da competência <strong>{competencia}</strong>.<br />
                  <strong>Rubricas anteriores</strong> geradas por ponto <strong>serão substituídas</strong>.
                </p>
              </div>
              <Button onClick={aplicar} disabled={applying || result.n_com_match === 0} data-testid="ponto-aplicar">
                {applying ? <Spinner size={14} /> : <CheckCircle2 size={14} />} Aplicar na folha
              </Button>
            </div>
            {applyResult && (
              <div className="mt-4 p-3 bg-emerald-50 border border-emerald-300 rounded-md">
                <div className="text-sm font-semibold text-emerald-900 mb-1">
                  ✔ {applyResult.aplicados} funcionário(s) atualizado(s)
                </div>
                {applyResult.ignorados?.length > 0 && (
                  <div className="text-xs text-amber-800 mt-2">
                    <div className="font-semibold mb-1"><AlertCircle size={12} className="inline mr-1" />Ignorados:</div>
                    <ul className="list-disc list-inside">
                      {applyResult.ignorados.map((i, ix) => (
                        <li key={ix}>{i.nome || i.funcionario_id}: {i.motivo}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </Card>
        </>
      )}

      {editDay && (
        <EditMarcsModal
          dia={editDay.dia}
          onClose={() => setEditDay(null)}
          onSave={saveEditDay}
        />
      )}
    </Layout>
  );
}

function EditMarcsModal({ dia, onClose, onSave }) {
  const [marcs, setMarcs] = useState(() => {
    // extrai só HH:MM (sem segundos) para editar mais fácil
    return (dia.marcacoes || []).map((m) => m.slice(0, 5));
  });
  const [novaMarc, setNovaMarc] = useState("");

  const addMarc = () => {
    if (!novaMarc || !/^\d{2}:\d{2}$/.test(novaMarc)) {
      alert("Formato inválido. Use HH:MM (ex.: 17:00)");
      return;
    }
    const nova = novaMarc + ":00";
    setMarcs([...marcs, nova].sort());
    setNovaMarc("");
  };
  const removeMarc = (ix) => setMarcs(marcs.filter((_, i) => i !== ix));
  const updateMarc = (ix, val) => {
    const arr = [...marcs];
    arr[ix] = val.length === 5 ? val + ":00" : val;
    setMarcs(arr);
  };

  const handleSave = () => {
    // Normaliza — garante formato HH:MM:SS
    const normalizadas = marcs
      .filter(Boolean)
      .map((m) => {
        const parts = m.split(":");
        return `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}:${(parts[2] || "00").padStart(2, "0")}`;
      })
      .filter((m) => /^\d{2}:\d{2}:\d{2}$/.test(m))
      .sort();
    onSave(normalizadas);
  };

  const sugestoes = [
    { h: "07:00", label: "Entrada", cor: "bg-emerald-50 text-emerald-700 border-emerald-300" },
    { h: "11:00", label: "Saída almoço", cor: "bg-amber-50 text-amber-700 border-amber-300" },
    { h: "13:00", label: "Volta almoço", cor: "bg-amber-50 text-amber-700 border-amber-300" },
    { h: "17:00", label: "Saída fim", cor: "bg-emerald-50 text-emerald-700 border-emerald-300" },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink-200">
          <div>
            <h3 className="font-heading font-bold text-ink-900">Editar marcações</h3>
            <p className="text-xs text-ink-500">{dia.data.split("-").reverse().join("/")} · {dia.dia_semana} · {dia.tipo_dia}</p>
          </div>
          <button onClick={onClose} className="text-ink-500 p-1"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-4">
          {dia.aviso && (
            <div className="p-2 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-900 flex items-start gap-2">
              <AlertTriangle size={12} className="shrink-0 mt-0.5" />
              <span>{dia.aviso}</span>
            </div>
          )}

          {/* Sugestões rápidas */}
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-ink-500 mb-2">Atalhos</div>
            <div className="grid grid-cols-2 gap-2">
              {sugestoes.map((s) => {
                const jaTem = marcs.some((m) => m.startsWith(s.h));
                return (
                  <button
                    key={s.h}
                    onClick={() => !jaTem && setMarcs([...marcs, s.h + ":00"].sort())}
                    disabled={jaTem}
                    className={`px-2 py-2 rounded-md border text-xs font-medium ${jaTem ? "opacity-40 cursor-not-allowed" : `${s.cor} hover:opacity-80`}`}
                    data-testid={`sug-${s.h}`}
                  >
                    <div className="font-mono font-bold">{s.h}</div>
                    <div className="text-[10px]">{s.label}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Lista de marcações */}
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-ink-500 mb-2">Marcações do dia ({marcs.length})</div>
            {marcs.length === 0 ? (
              <div className="text-sm text-ink-500 italic p-3 bg-stone-50 rounded-lg">Sem marcações — adicione usando os atalhos acima ou o campo abaixo</div>
            ) : (
              <div className="space-y-1">
                {marcs.map((m, ix) => (
                  <div key={ix} className="flex items-center gap-2">
                    <span className="text-[10px] font-mono text-ink-500 w-4">{ix + 1}</span>
                    <input
                      type="time"
                      value={m.slice(0, 5)}
                      onChange={(e) => updateMarc(ix, e.target.value)}
                      className="flex-1 px-3 py-1.5 border border-ink-200 rounded-md text-sm font-mono"
                      data-testid={`marc-${ix}`}
                    />
                    <button onClick={() => removeMarc(ix)} className="p-1.5 rounded hover:bg-red-100 text-danger" title="Remover" data-testid={`remove-marc-${ix}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Adicionar avulsa */}
          <div className="flex gap-2 items-end pt-2 border-t border-ink-200">
            <div className="flex-1">
              <label className="text-[10px] font-bold uppercase tracking-wider text-ink-500">Adicionar horário manual</label>
              <input
                type="time"
                value={novaMarc}
                onChange={(e) => setNovaMarc(e.target.value)}
                className="w-full mt-1 px-3 py-1.5 border border-ink-200 rounded-md text-sm font-mono"
                data-testid="nova-marc"
              />
            </div>
            <button onClick={addMarc} className="px-3 py-2 bg-brand-500 text-white rounded-md text-sm hover:bg-brand-600" data-testid="add-marc">
              <Plus size={14} className="inline" /> Add
            </button>
          </div>
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-ink-200">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-ink-200 text-sm hover:bg-stone-50">Cancelar</button>
          <button onClick={handleSave} className="px-4 py-2 rounded-md bg-brand-500 text-white text-sm hover:bg-brand-600 font-medium" data-testid="save-marcs">
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, tone = "default" }) {
  const tones = { default: "text-ink-900", ok: "text-ok", danger: "text-danger", warn: "text-warn", neutral: "text-ink-700" };
  return (
    <div className="p-4 rounded-lg border bg-white border-ink-200">
      <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-500">{label}</div>
      <div className={`font-mono text-2xl font-semibold mt-1 ${tones[tone]}`}>{value}</div>
    </div>
  );
}
